import {AppDispatch, store} from "../redux/store";
import {XmUIHandler} from "../xm/js-es6/xmui-es6.js";
import {
  AppState,
  updateCurrentStateWithConfirmationInputFormProps,
  updateCurrentStateWithInformationResponseProps,
  updateCurrentStateWithProcessJsonDataProps,
  updateCurrentStateWithSelectionUIProps,
  updateCurrentUIHandlerFunction,
  updateRedirectionUserId
} from "../redux/AppStateSlice";
import {CustomFormSession} from "./CustomFormSession";
import {PasswordAuthenticatorSession} from "./PasswordAuthenticatorSession";
import {OtpAuthenticationSession} from "./OtpAuthenticationSession";
import {getTitle} from "./helpers";
import {getLogger} from "../logConfig";
import {updateSpinner} from "@spectrumselect/mi-ui-react/build/components/loaders/spinner/Spinner";
import {S} from "./sdk";

const logger = getLogger("CustomUiHandler");

// @ts-ignore-next-line
export class CustomUiHandler extends XmUIHandler {
  dispatch: AppDispatch;

  constructor(dispatch: AppDispatch) {
    super();
    this.dispatch = dispatch;
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startActivityIndicator(actionContext: com.ts.mobile.sdk.PolicyAction | null, clientContext: object | null): void {
    updateSpinner({show: true});
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  endActivityIndicator(actionContext: com.ts.mobile.sdk.PolicyAction | null, clientContext: object | null): void {
    updateSpinner({show: false});
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getInformationResponse(title: string, text: string, buttonText: string, actionContext: com.ts.mobile.sdk.PolicyAction | null, clientContext: object | null): Promise<com.ts.mobile.sdk.ConfirmationInput> {
    this.dispatch(updateCurrentUIHandlerFunction("getInformationResponse"));
    if (buttonText && buttonText.length > 0) {
      return new Promise<com.ts.mobile.sdk.ConfirmationInput>((resolve) => {
        this.dispatch(updateCurrentStateWithInformationResponseProps({
          state: AppState.INFORMATION_RESPONSE,
          props: {title, text, buttonText, resolve},
        }))
      });
    } else {
      return new Promise<com.ts.mobile.sdk.ConfirmationInput>((resolve) => {
        resolve(com.ts.mobile.sdk.ConfirmationInput.create(-1));
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getConfirmationInput(title: string, text: string, continueText: string, cancelText: string, actionContext: com.ts.mobile.sdk.PolicyAction | null, clientContext: object | null): Promise<com.ts.mobile.sdk.ConfirmationInput> {
    this.dispatch(updateCurrentUIHandlerFunction("getConfirmationInput"));
    return new Promise((resolve) => {
      this.dispatch(updateCurrentStateWithConfirmationInputFormProps({
        state: AppState.CONFIRMATION_INPUT,
        props: {title, text, continueText, cancelText, resolve},
      }))
    })
  }

  //Add the following method override
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectAuthenticator(options: Array<com.ts.mobile.sdk.AuthenticationOption>, actionContext: com.ts.mobile.sdk.PolicyAction | null, clientContext: object | null): Promise<com.ts.mobile.sdk.AuthenticatorSelectionResult> {
    this.dispatch(updateCurrentUIHandlerFunction("selectAuthenticator"));
    const authOptions = options.map((option) => {
      return {
        title: getTitle(option, actionContext),
        value: S.AuthenticatorSelectionResult.createSelectionRequest(option.getAuthenticator())
      };
    });
    const cancelRequest = S.AuthenticatorSelectionResult.createAbortRequest();

    return new Promise<com.ts.mobile.sdk.AuthenticatorSelectionResult>((resolve) => {
      this.dispatch(updateCurrentStateWithSelectionUIProps({
        state: AppState.SELECTION_UI,
        props: {items: authOptions, instructions: "Please choose an Authenticator", cancelRequest, resolve},
      }));
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  shouldIncludeDisabledAuthenticatorsInMenu(actionContext: com.ts.mobile.sdk.PolicyAction | null, clientContext: object | null): boolean {
    this.dispatch(updateCurrentUIHandlerFunction("shouldIncludeDisabledAuthenticatorsInMenu"));
    return false;
  }

  createPasswordAuthSession(title: string, username: string): com.ts.mobile.sdk.UIAuthenticatorSession<com.ts.mobile.sdk.PasswordInput> {
    this.dispatch(updateCurrentUIHandlerFunction("createPasswordAuthSession"));
    return new PasswordAuthenticatorSession(title, username, this.dispatch);
  }

  createOtpAuthSession(title: string, username: string, possibleTargets: Array<com.ts.mobile.sdk.OtpTarget>, autoExecedTarget: com.ts.mobile.sdk.OtpTarget | null): com.ts.mobile.sdk.UIAuthenticatorSessionOtp {
    this.dispatch(updateCurrentUIHandlerFunction("createOtpAuthSession"));
    return new OtpAuthenticationSession(title, username, possibleTargets, autoExecedTarget, this.dispatch);
  }

  controlOptionForCancellationRequestInSession(validOptions: Array<com.ts.mobile.sdk.ControlRequestType>, session: com.ts.mobile.sdk.UIAuthenticatorSession<com.ts.mobile.sdk.InputResponseType>): Promise<com.ts.mobile.sdk.ControlRequest> {
    this.dispatch(updateCurrentUIHandlerFunction("controlOptionForCancellationRequestInSession"));
    return (session as any).promiseCancelAction(validOptions);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  processJsonData(jsonData: object, actionContext: com.ts.mobile.sdk.PolicyAction | null, clientContext: object | null): Promise<com.ts.mobile.sdk.JsonDataProcessingResult> {
    this.dispatch(updateCurrentUIHandlerFunction("processJsonData"));
    return new Promise((resolve) => {
      this.dispatch(updateCurrentStateWithProcessJsonDataProps({
        state: AppState.PROCESS_JSON,
        props: {jsonData, actionContext, resolve},
      }))
    })
  }

  createFormSession(formId: string, payload: object): com.ts.mobile.sdk.UIFormSession {
    this.dispatch(updateCurrentUIHandlerFunction("createFormSession"));
    return new CustomFormSession(formId, payload, this.dispatch);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handlePolicyRedirect(redirectType: com.ts.mobile.sdk.RedirectType, policyId: string | null, userId: string | null, additionalParameters: object | null, clientContext: object | null): Promise<com.ts.mobile.sdk.RedirectInput> {
    this.dispatch(updateCurrentUIHandlerFunction("handlePolicyRedirect"));
    logger.info("Policy redirection requested. redirecting to policy: [" + policyId + "] with userId: [" + userId + "] and additionalParameters: [" + additionalParameters + "]");
    this.dispatch(updateRedirectionUserId(userId || ""));

    return Promise.resolve(S.RedirectInput.create(S.RedirectResponseType.RedirectToPolicy));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handlePolicyRejection(title: string | null, text: string | null, buttonText: string | null, failureData: object | null, actionContext: com.ts.mobile.sdk.PolicyAction | null, clientContext: object | null): Promise<com.ts.mobile.sdk.ConfirmationInput> {
    this.dispatch(updateCurrentUIHandlerFunction("handlePolicyRejection"));
    // if we were on the RegistrationInformation page and the policy gets rejected, we want to go to the login page
    if (store.getState().appState.currentState === AppState.REGISTRATION_INFORMATION) {
      return new Promise<com.ts.mobile.sdk.ConfirmationInput>((resolve) => {
        resolve(S.ConfirmationInput.create(-1 as any));
      });
    } else if (buttonText && buttonText.length > 0) {
      return new Promise<com.ts.mobile.sdk.ConfirmationInput>((resolve) => {
        this.dispatch(updateCurrentStateWithInformationResponseProps({
          state: AppState.INFORMATION_RESPONSE,
          props: {title: title || "", text: text || "", buttonText: buttonText || "", resolve},
        }));
      });
    } else {
      return new Promise<com.ts.mobile.sdk.ConfirmationInput>((resolve) => {
        resolve(com.ts.mobile.sdk.ConfirmationInput.create(-1));
      })
    }
  }
}